'use client';

import React, { useMemo } from 'react';
import { useParams } from 'next/navigation';
import {
  useGetTargetStrategyProviderDetailsQuery,
  useGetTargetStrategyDetailsQuery,
  useGetChainedStrategyDetailsQuery,
  useGetStrategyMetricsQuery,
} from 'src/api/redux';
import { CircularProgress } from 'src/components';
import { PulseButton } from 'src/components/PulseUI';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useSite } from 'src/context/providers/SiteProvider';
import { useProfileStrategies } from 'src/hooks/strategy';
import { StrategyInfoDrawer, StrategyFreshFacesBadge } from 'src/components/Strategy/shared';
import { StrategyProfileMetricItem } from '../StrategyProfileMetricItem';
import {
  getMetricValue,
  ProviderMetricsData,
  MetricLocation,
} from '../../utils';

const StrategyProfileMetrics: React.FC = () => {
  const { id, strategyId } = useParams<{ id: string; strategyId: string }>();
  // Fetch chain details to get the target strategy ID
  const { data: chainData, isLoading: isChainLoading } =
    useGetChainedStrategyDetailsQuery(strategyId, {
      skip: !strategyId,
    });
  const { openDrawer, closeAllButCurrent, isDrawerOpen, closeDrawer } = useDrawer();
  const { dispatch } = useSite();
  const { setSelectedProvider, setActiveMarker } = useProfileStrategies();
  const drawerId = 'profile-strategy-info';

  // Extract strategy ID from chain
  const innerStrategyId = useMemo(() => {
    return chainData?.target_strategy?.id?.toString();
  }, [chainData]);

  // Fetch full target strategy details to check for code_set
  const { data: targetStrategyDetails } = useGetTargetStrategyDetailsQuery(
    innerStrategyId,
    { skip: !innerStrategyId }
  );

  // Fetch strategy metrics (headers/labels)
  const { data: strategyMetrics, isLoading: isMetricsLoading } =
    useGetStrategyMetricsQuery(
      {
        strategyType: 'target',
        strategyId: innerStrategyId,
      },
      {
        skip: !innerStrategyId,
      }
    );

  // Fetch provider-specific metric values
  const { data: providerDetails, isLoading: isProviderLoading } =
    useGetTargetStrategyProviderDetailsQuery(
      {
        strategyId: innerStrategyId,
        providerId: id,
      },
      {
        skip: !innerStrategyId || !id,
      }
    );

  const selectedProvider: ProviderMetricsData | undefined = providerDetails;

  if (isChainLoading || isMetricsLoading || isProviderLoading) {
    return (
      <div className="self-stretch p-4 bg-white rounded-lg border border-neutral-200 inline-flex flex-col justify-center items-center gap-4">
        <CircularProgress size="md" color="primary" />
      </div>
    );
  }

  if (!strategyMetrics?.results?.length) {
    return (
      <div className="self-stretch p-4 bg-white rounded-lg border border-neutral-200 inline-flex flex-col justify-center items-center gap-4">
        <div className="text-text-tertiary text-sm">No metrics available</div>
      </div>
    );
  }

  const handleStrategyInfoClick = () => {
    openDrawer(drawerId);
  };

  const handleCloseDrawer = () => {
    closeDrawer(drawerId);
  };

  const handleLocationClick = (location: MetricLocation) => {
    setSelectedProvider(location as any);
    setActiveMarker(location.provider_id?.toString());
    dispatch({
      type: 'SET_CURRENT_PROVIDER',
      payload: { id: location.provider_id, type: location.type || null },
    });
    closeAllButCurrent('profile');
  };

  // Get active strategy details based on type
  const strategyDetails = targetStrategyDetails;
  const isFreshFaces = strategyDetails?.type === 'FRESH_FACES';
  const label = strategyDetails?.label || '';
  const description = strategyDetails?.description || '';

  return (
    <div className="flex flex-col gap-4 w-full">
      {chainData?.target_strategy && (
        <div className="self-stretch inline-flex justify-start items-center gap-2">
          <div className="flex-1 inline-flex flex-col justify-center items-start gap-0.5">
            <div className="flex items-center gap-2 text-primary text-lg font-bold font-['Inter'] leading-7 line-clamp-1">
              {label}
              {isFreshFaces && <StrategyFreshFacesBadge />}
            </div>
            <div className="self-stretch justify-start text-secondary text-sm font-normal font-['Inter'] leading-5 line-clamp-1">
              {description}
            </div>
          </div>
          <PulseButton
            variant="soft"
            color="neutral"
            startDecorator={faInfoCircle}
            onClick={handleStrategyInfoClick}
            className="h-9 mt-1.5 bg-neutral-500 hover:bg-neutral-400 [&>span]:!justify-center [&>span]:gap-2 [&>span>span:first-child]:pl-0 [&_svg]:text-sm rounded-full text-white font-bold"
          >
            Strategy Info
          </PulseButton>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 w-full">
        {strategyMetrics.results.map((header, index) => (
          <StrategyProfileMetricItem
            key={header.id}
            header={header}
            index={index}
            selectedProvider={selectedProvider}
            strategyLabel={strategyDetails?.code_set?.label}
            isFreshFaces={isFreshFaces}
            onLocationClick={handleLocationClick}
            getMetricValue={getMetricValue}
          />
        ))}
      </div>
      <StrategyInfoDrawer
        isOpen={isDrawerOpen(drawerId)}
        onClose={handleCloseDrawer}
        chainData={chainData}
        isLoading={isChainLoading}
      />
    </div>
  );
};

export default StrategyProfileMetrics;
